/**
 * Color palettes for isometric art.
 * Each material has a 4-shade ramp: highlight, base, shadow, dark.
 * Consistent top-left light source.
 */

export interface ColorRamp {
  highlight: number;
  base: number;
  shadow: number;
  dark: number;
}

// ─── Materials ───────────────────────────────────────────────

export const WOOD_LIGHT: ColorRamp = { highlight: 0xd4a056, base: 0xb8862a, shadow: 0x8b6914, dark: 0x6b4a0a };
export const WOOD_DARK: ColorRamp = { highlight: 0xa07040, base: 0x7a5030, shadow: 0x5a3820, dark: 0x3a2010 };
export const WOOD_RED: ColorRamp = { highlight: 0xc06040, base: 0x984830, shadow: 0x703020, dark: 0x502010 };

export const METAL_GREY: ColorRamp = { highlight: 0xb0b0c0, base: 0x8888a0, shadow: 0x606080, dark: 0x404060 };
export const METAL_DARK: ColorRamp = { highlight: 0x707088, base: 0x505068, shadow: 0x383850, dark: 0x282840 };

export const PLASTIC_WHITE: ColorRamp = { highlight: 0xf0f0f0, base: 0xd0d0d8, shadow: 0xa0a0b0, dark: 0x808090 };
export const PLASTIC_BLACK: ColorRamp = { highlight: 0x505058, base: 0x383840, shadow: 0x282830, dark: 0x181820 };

export const FABRIC_BLUE: ColorRamp = { highlight: 0x5080c0, base: 0x3868a8, shadow: 0x284888, dark: 0x183060 };
export const FABRIC_RED: ColorRamp = { highlight: 0xc05050, base: 0xa03838, shadow: 0x802828, dark: 0x601818 };
export const FABRIC_GREEN: ColorRamp = { highlight: 0x50a050, base: 0x388838, shadow: 0x286828, dark: 0x184818 };

export const SCREEN_ON: ColorRamp = { highlight: 0x88ccff, base: 0x4488cc, shadow: 0x2266aa, dark: 0x114488 };
export const SCREEN_OFF: ColorRamp = { highlight: 0x405060, base: 0x303848, shadow: 0x202830, dark: 0x101820 };

export const PLANT_GREEN: ColorRamp = { highlight: 0x60c040, base: 0x48a030, shadow: 0x308020, dark: 0x206010 };
export const PLANT_DARK: ColorRamp = { highlight: 0x408030, base: 0x306820, shadow: 0x205010, dark: 0x103808 };
export const POT_BROWN: ColorRamp = { highlight: 0xc08050, base: 0xa06838, shadow: 0x805028, dark: 0x603818 };

export const CERAMIC_WHITE: ColorRamp = { highlight: 0xf8f8ff, base: 0xe0e0e8, shadow: 0xb8b8c8, dark: 0x9898a8 };

// ─── Floor Palettes (per office level) ───────────────────────

export interface FloorPalette {
  tile: ColorRamp;
  tileAlt: ColorRamp;  // Alternating tiles for checker pattern
  gridLine: number;
}

export const FLOOR_GARAGE: FloorPalette = {
  tile: { highlight: 0x4a4a5e, base: 0x3c3c50, shadow: 0x2e2e42, dark: 0x222236 },
  tileAlt: { highlight: 0x444458, base: 0x38384c, shadow: 0x2a2a3e, dark: 0x1e1e32 },
  gridLine: 0x2a2a3e,
};

export const FLOOR_OFFICE: FloorPalette = {
  tile: { highlight: 0x6a7a8a, base: 0x586878, shadow: 0x465868, dark: 0x344858 },
  tileAlt: { highlight: 0x627282, base: 0x506070, shadow: 0x3e5060, dark: 0x2c4050 },
  gridLine: 0x3e5060,
};

export const FLOOR_SUITE: FloorPalette = {
  tile: { highlight: 0x8a8070, base: 0x786e5e, shadow: 0x665c4e, dark: 0x544a3e },
  tileAlt: { highlight: 0x807668, base: 0x6e6458, shadow: 0x5c5248, dark: 0x4a4038 },
  gridLine: 0x5c5248,
};

export const FLOOR_PREMIUM: FloorPalette = {
  tile: { highlight: 0x607080, base: 0x4e5e6e, shadow: 0x3c4c5c, dark: 0x2a3a4a },
  tileAlt: { highlight: 0x586878, base: 0x465666, shadow: 0x344454, dark: 0x223242 },
  gridLine: 0x344454,
};

export const FLOOR_HQ: FloorPalette = {
  tile: { highlight: 0x708898, base: 0x5e7688, shadow: 0x4c6478, dark: 0x3a5268 },
  tileAlt: { highlight: 0x688090, base: 0x566e80, shadow: 0x445c70, dark: 0x324a60 },
  gridLine: 0x445c70,
};

export const FLOOR_PALETTES: FloorPalette[] = [
  FLOOR_GARAGE,
  FLOOR_OFFICE,
  FLOOR_SUITE,
  FLOOR_PREMIUM,
  FLOOR_HQ,
];
